import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { adminPaymentsService, type PaymentTransaction, type TransactionActionResponse } from '@/services/adminPayments';
import { useToast } from '@/hooks/use-toast';
import { useRolePermissions } from '@/hooks/use-role-permissions';

function AmountDialog({
  open,
  title,
  defaultAmount,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  defaultAmount?: number;
  onClose: () => void;
  onConfirm: (amount?: number) => void;
}) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState<string>(defaultAmount ? String(defaultAmount) : '');
  useEffect(() => {
    setAmount(defaultAmount ? String(defaultAmount) : '');
  }, [defaultAmount, open]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label>{t('amount')} ({t('optional') || 'optional'})</Label>
          <Input
            type="number"
            min={0}
            placeholder={defaultAmount ? `${t('defaultToFullAmount')}: ${defaultAmount}` : t('enterAmount')}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <div className="text-xs text-muted-foreground">
            {t('defaultToFullAmount')}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>{t('cancel')}</Button>
          <Button onClick={() => onConfirm(amount ? Number(amount) : undefined)}>{t('confirm') || 'Confirm'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function TransactionDetails() {
  const { t } = useTranslation();
  const params = useParams<{ id: string }>();
  const id = params?.id as string;
  const { toast } = useToast();
  const { canManage } = useRolePermissions();
  const canEdit = canManage('payments');

  const [loading, setLoading] = useState(false);
  const [tx, setTx] = useState<PaymentTransaction | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [result, setResult] = useState<TransactionActionResponse | null>(null);

  const [captureOpen, setCaptureOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await adminPaymentsService.getTransaction(id);
      if (res.success && res.data) {
        setTx(res.data);
      } else {
        setTx(null);
      }
    } catch (e: any) {
      toast({ title: t('error'), description: e?.response?.data?.message || e?.message || t('failedToLoadTransaction'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const canCapture = tx?.status === 'authorized';
  const canCancel = tx?.status === 'pending' || tx?.status === 'authorized';
  const canRefund = tx?.status === 'paid';

  const doCapture = async (amount?: number) => {
    try {
      setActionLoading(true);
      const res = await adminPaymentsService.capture(id, amount ? { amount } : undefined);
      setResult(res.data || null);
      toast({ title: t('transactionCaptured'), description: res.message || t('transactionCaptured') });
      await load();
    } catch (e: any) {
      toast({ title: t('error'), description: e?.response?.data?.message || e?.message || t('failedToCapture'), variant: 'destructive' });
    } finally {
      setActionLoading(false);
      setCaptureOpen(false);
    }
  };

  const doCancel = async () => {
    try {
      setActionLoading(true);
      const res = await adminPaymentsService.cancel(id);
      setResult(res.data || null);
      toast({ title: t('transactionCancelled'), description: res.message || t('transactionCancelled') });
      await load();
    } catch (e: any) {
      toast({ title: t('error'), description: e?.response?.data?.message || e?.message || t('failedToCancel'), variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  const doRefund = async (amount?: number) => {
    try {
      setActionLoading(true);
      const res = await adminPaymentsService.refund(id, amount ? { amount } : undefined);
      setResult(res.data || null);
      toast({ title: t('transactionRefunded'), description: res.message || t('transactionRefunded') });
      await load();
    } catch (e: any) {
      toast({ title: t('error'), description: e?.response?.data?.message || e?.message || t('failedToRefund'), variant: 'destructive' });
    } finally {
      setActionLoading(false);
      setRefundOpen(false);
    }
  };

  const doReconcile = async () => {
    try {
      setActionLoading(true);
      const res = await adminPaymentsService.reconcile(id);
      setResult(res.data || null);
      toast({ title: t('transactionReconciled'), description: res.message || t('transactionReconciled') });
      await load();
    } catch (e: any) {
      toast({ title: t('error'), description: e?.response?.data?.message || e?.message || t('failedToReconcile'), variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('transactionDetails')}</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-8 text-center text-muted-foreground">{t('loadingTransaction')}</div>
          ) : !tx ? (
            <div className="py-8 text-center text-muted-foreground">{t('notFound') || 'Not found'}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <div className="text-sm text-muted-foreground">{t('transactionId')}</div>
                <div className="font-medium">#{tx.id}</div>
                <div className="text-sm text-muted-foreground">{t('status')}</div>
                <div>
                  <Badge variant="outline">{tx.status}</Badge>
                </div>
                <div className="text-sm text-muted-foreground">{t('provider')}</div>
                <div>{tx.provider_code}</div>
                <div className="text-sm text-muted-foreground">{t('method')}</div>
                <div>{tx.method || tx.payment_method?.provider_name || '-'}</div>
                <div className="text-sm text-muted-foreground">{t('amount')}</div>
                <div>{tx.amount}</div>
                <div className="text-sm text-muted-foreground">{t('createdAt')}</div>
                <div>{new Date(tx.createdAt).toLocaleString()}</div>
              </div>
              <div className="space-y-2">
                <div className="text-sm text-muted-foreground">{t('booking')}</div>
                <div>{tx.booking_id || tx.booking?.id || '-'}</div>
                <div className="text-sm text-muted-foreground">{t('user')}</div>
                <div>{tx.user_id || tx.user?.id || '-'}</div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('actions')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button disabled={!canEdit || !canCapture || actionLoading} onClick={() => setCaptureOpen(true)}>{t('capture')}</Button>
            <Button variant="secondary" disabled={!canEdit || !canCancel || actionLoading} onClick={doCancel}>{t('cancel')}</Button>
            <Button variant="outline" disabled={!canEdit || !canRefund || actionLoading} onClick={() => setRefundOpen(true)}>{t('refund')}</Button>
            <Button variant="ghost" disabled={actionLoading} onClick={doReconcile}>{t('reconcile')}</Button>
          </div>
          {result && (
            <div className="mt-6">
              <div className="text-sm font-medium mb-2">{t('actionResults')}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div><span className="text-muted-foreground">{t('step')}:</span> {result.step}</div>
                <div><span className="text-muted-foreground">{t('responseStatus')}:</span> {result.status}</div>
                <div><span className="text-muted-foreground">{t('actionRequired')}:</span> {result.action_required || '-'}</div>
                <div><span className="text-muted-foreground">{t('paymentState')}:</span> {result.payment_state || '-'}</div>
                <div><span className="text-muted-foreground">{t('internalOrderState')}:</span> {result.internal_order_state || '-'}</div>
                <div className="md:col-span-2">
                  <div className="text-muted-foreground">{t('providerResponse')}</div>
                  <pre className="mt-2 p-3 bg-muted rounded overflow-x-auto text-xs">
{`${
  JSON.stringify(result.provider_response ?? {}, null, 2)
}`}
                  </pre>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <AmountDialog
        open={captureOpen}
        title={t('captureTransaction')}
        defaultAmount={tx?.amount}
        onClose={() => setCaptureOpen(false)}
        onConfirm={doCapture}
      />
      <AmountDialog
        open={refundOpen}
        title={t('refundTransaction')}
        defaultAmount={tx?.amount}
        onClose={() => setRefundOpen(false)}
        onConfirm={doRefund}
      />
    </div>
  );
}


